// Pago a Proveedores · consultas y autorizaciones (docs/108 B5): autorizar/rechazar (otra persona) y
// registrar pagado con comprobante; CxP por proveedor; cartola del proveedor; historial con anulación.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { api } from '../api/cliente';
import { useIntentos } from '../api/idempotencia';
import { PanelError } from '../comun/Estados';
import { Grilla, type Columna } from '../comun/Grilla';
import { Modal } from '../comun/Modal';
import { fecha, fechaHora, pesos } from '../comun/formato';
import { useSesion } from '../sesion/SesionContexto';
import { BuscadorCliente } from '../ventas/BuscadorCliente';
import {
  clavesPagos,
  NOMBRE_ESTADO_PAGO,
  useCartolaProveedor,
  usePagos,
  useResumenCxp,
  type EstadoPago,
  type PagoProveedor,
  type ResumenProveedor,
} from './datosPagos';
import { ImprimirCartolaProveedor, ImprimirComprobanteEgreso } from './Impresos';

function useAccionPago(empresaId: string) {
  const qc = useQueryClient();
  const intentos = useIntentos();
  return useMutation({
    mutationFn: ({
      pagoId,
      accion,
      cuerpo,
    }: {
      pagoId: string;
      accion: string;
      cuerpo: Record<string, unknown>;
    }) =>
      api.post(`/tesoreria/pagos/${pagoId}/${accion}`, cuerpo, {
        idempotencia: intentos.clave(`pago-${accion}`, { pagoId, ...cuerpo }),
      }),
    onSuccess: async () => {
      intentos.cerrar();
      await qc.invalidateQueries({ queryKey: clavesPagos.pagos(empresaId) });
    },
    onError: (e) => intentos.cerrar(e),
  });
}

const columnasPago = (proveedor: (id: string) => string): Columna<PagoProveedor>[] => [
  { id: 'n', titulo: 'N°', celda: (p) => <span className="mono">{p.numero}</span>, ancho: '4rem' },
  { id: 'fecha', titulo: 'Solicitado', celda: (p) => fechaHora(p.solicitadoEn), ancho: '10rem' },
  { id: 'prov', titulo: 'Proveedor', celda: (p) => proveedor(p.proveedorEntidadId) },
  {
    id: 'medio',
    titulo: 'Medio y cuentas',
    // Desde qué cuenta de la empresa y a qué cuenta del proveedor (2026-10-19).
    celda: (p) => (
      <span
        className="pagado-con"
        title={[p.cuentaOrigen, p.cuentaDestino].filter(Boolean).join(' → ')}
      >
        <span>{p.medio.toLowerCase().replace('_', ' ')}</span>
        {p.cuentaOrigen && <small>desde {p.cuentaOrigen}</small>}
        {p.cuentaDestino && <small>a {p.cuentaDestino}</small>}
      </span>
    ),
    ancho: '16rem',
  },
  { id: 'prog', titulo: 'Programado', celda: (p) => fecha(p.fechaProgramada), ancho: '7rem' },
  {
    id: 'docs',
    titulo: 'Documentos',
    celda: (p) =>
      p.aplicaciones
        .filter((a) => a.tipo === 'APLICACION' && !a.reversada)
        .map((a) => a.folio)
        .concat((p.notasCredito ?? []).map((x) => `NC ${x.ncFolio ?? ''} → ${x.folio ?? ''}`))
        .join(', ') || 'anticipo',
    ancho: '10rem',
  },
  {
    id: 'total',
    titulo: 'Total',
    celda: (p) => <b>{pesos(p.total)}</b>,
    alinear: 'der',
    ancho: '8rem',
  },
  { id: 'estado', titulo: 'Estado', celda: (p) => NOMBRE_ESTADO_PAGO[p.estado], ancho: '8rem' },
];

function useNombresProveedores(pagos: PagoProveedor[] | undefined) {
  const { empresa } = useSesion();
  const resumen = useResumenCxp(empresa!.id);
  const nombres = new Map((resumen.data ?? []).map((r) => [r.proveedorEntidadId, r.proveedor]));
  return (id: string) => nombres.get(id) ?? (pagos ? id.slice(0, 8) : '');
}

// ================================================================ autorizaciones

export function AutorizacionesPago() {
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const porAutorizar = usePagos(empresaId, { estado: 'PENDIENTE_AUTORIZACION' });
  const autorizados = usePagos(empresaId, { estado: 'AUTORIZADO' });
  const proveedor = useNombresProveedores(porAutorizar.data);
  const accion = useAccionPago(empresaId);
  const [rechazo, setRechazo] = useState<PagoProveedor | null>(null);
  const [pagar, setPagar] = useState<PagoProveedor | null>(null);
  const [texto, setTexto] = useState('');
  const columnas = columnasPago(proveedor);
  return (
    <div className="pantalla consulta" data-pantalla="autorizaciones-pago">
      <h3 className="titulo-seccion">Por autorizar</h3>
      <p className="apagado">Quien solicitó un pago no lo autoriza: lo autoriza otra persona.</p>
      <Grilla
        etiqueta="Pagos por autorizar"
        columnas={[
          ...columnas,
          {
            id: 'acc',
            titulo: '',
            celda: (p) =>
              puede('tesoreria.pago.autorizar') ? (
                <span className="acciones-fila">
                  <button
                    type="button"
                    className="boton-mini primario"
                    onClick={() =>
                      accion.mutate({
                        pagoId: p.pagoId,
                        accion: 'resolver',
                        cuerpo: { version: p.version, autorizar: true },
                      })
                    }
                  >
                    Autorizar
                  </button>
                  <button type="button" className="boton-mini" onClick={() => setRechazo(p)}>
                    Rechazar
                  </button>
                </span>
              ) : null,
            ancho: '12rem',
          },
        ]}
        filas={porAutorizar.data ?? []}
        clave={(p) => p.pagoId}
        cargando={porAutorizar.isLoading}
        vacio="No hay pagos por autorizar"
      />
      <h3 className="titulo-seccion">Autorizados por pagar</h3>
      <Grilla
        etiqueta="Pagos autorizados"
        columnas={[
          ...columnas,
          {
            id: 'acc',
            titulo: '',
            celda: (p) =>
              puede('tesoreria.pago.ejecutar') ? (
                <button type="button" className="boton-mini primario" onClick={() => setPagar(p)}>
                  Registrar pagado
                </button>
              ) : null,
            ancho: '10rem',
          },
        ]}
        filas={autorizados.data ?? []}
        clave={(p) => p.pagoId}
        cargando={autorizados.isLoading}
        vacio="No hay pagos autorizados pendientes"
      />
      {accion.isError && <PanelError error={accion.error} />}
      {(rechazo || pagar) && (
        <Modal
          titulo={
            rechazo ? `Rechazar pago N° ${rechazo.numero}` : `Registrar pago N° ${pagar!.numero}`
          }
          alCerrar={() => {
            setRechazo(null);
            setPagar(null);
            setTexto('');
          }}
        >
          <label className="campo">
            <span className="etiqueta">
              {rechazo
                ? 'Motivo del rechazo'
                : 'Comprobante (N° transferencia, cheque o vale vista)'}
            </span>
            <input
              aria-label={rechazo ? 'Motivo del rechazo' : 'Comprobante'}
              autoFocus
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
            />
          </label>
          {accion.isError && <PanelError error={accion.error} />}
          <div className="acciones-modal">
            <button
              type="button"
              onClick={() => {
                setRechazo(null);
                setPagar(null);
                setTexto('');
              }}
            >
              Volver
            </button>
            <button
              type="button"
              className="primario"
              disabled={
                // Un pago en $0 (todo con NC) no tiene comprobante bancario.
                (rechazo || Number(pagar!.total) > 0
                  ? texto.trim().length < (rechazo ? 5 : 3)
                  : false) || accion.isPending
              }
              onClick={() =>
                accion.mutate(
                  rechazo
                    ? {
                        pagoId: rechazo.pagoId,
                        accion: 'resolver',
                        cuerpo: {
                          version: rechazo.version,
                          autorizar: false,
                          motivo: texto.trim(),
                        },
                      }
                    : {
                        pagoId: pagar!.pagoId,
                        accion: 'pagar',
                        cuerpo: { version: pagar!.version, comprobante: texto.trim() },
                      },
                  {
                    onSuccess: () => {
                      setRechazo(null);
                      setPagar(null);
                      setTexto('');
                    },
                  },
                )
              }
            >
              {rechazo ? 'Rechazar' : 'Registrar pagado'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ================================================================ CxP por proveedor

export function CxpResumen({
  alAbrirCartola,
}: {
  alAbrirCartola: (id: string, nombre: string) => void;
}) {
  const { empresa } = useSesion();
  const q = useResumenCxp(empresa!.id);
  const total = (k: 'saldo' | 'vencido' | 'comprometido') =>
    (q.data ?? []).reduce((s, r) => s + Number(r[k]), 0);
  const columnas: Columna<ResumenProveedor>[] = [
    { id: 'prov', titulo: 'Proveedor', celda: (r) => r.proveedor },
    {
      id: 'rut',
      titulo: 'RUT',
      celda: (r) => <span className="mono">{r.rut}</span>,
      ancho: '9rem',
    },
    { id: 'docs', titulo: 'Docs', celda: (r) => r.documentos, alinear: 'der', ancho: '4rem' },
    {
      id: 'saldo',
      titulo: 'Saldo',
      celda: (r) => <b>{pesos(r.saldo)}</b>,
      alinear: 'der',
      ancho: '9rem',
    },
    {
      id: 'vencido',
      titulo: 'Vencido',
      celda: (r) => (
        <span className={Number(r.vencido) ? 'tono-malo' : ''}>{pesos(r.vencido)}</span>
      ),
      alinear: 'der',
      ancho: '9rem',
    },
    {
      id: 'comp',
      titulo: 'En pagos en curso',
      celda: (r) => (Number(r.comprometido) ? pesos(r.comprometido) : ''),
      alinear: 'der',
      ancho: '9rem',
    },
    {
      id: 'ant',
      titulo: 'Anticipos',
      celda: (r) => (Number(r.anticipos) ? pesos(r.anticipos) : ''),
      alinear: 'der',
      ancho: '8rem',
    },
  ];
  return (
    <div className="pantalla consulta" data-pantalla="cxp">
      <div className="barra-filtros">
        <span className="espaciador" />
        <span>
          Saldo <b>{pesos(String(total('saldo')))}</b> · vencido{' '}
          <b className="tono-malo">{pesos(String(total('vencido')))}</b> · en pagos en curso{' '}
          <b>{pesos(String(total('comprometido')))}</b>
        </span>
      </div>
      {q.isError && <PanelError error={q.error} />}
      <Grilla
        etiqueta="Cuentas por pagar por proveedor"
        columnas={columnas}
        filas={q.data ?? []}
        clave={(r) => r.proveedorEntidadId}
        cargando={q.isLoading}
        vacio="No hay cuentas por pagar abiertas"
        alAbrir={(r) => alAbrirCartola(r.proveedorEntidadId, r.proveedor)}
      />
    </div>
  );
}

// ================================================================ cartola del proveedor

export function CartolaProveedor({
  proveedorInicial,
}: {
  proveedorInicial?: { id: string; razonSocial: string } | undefined;
}) {
  const { empresa } = useSesion();
  const [proveedor, setProveedor] = useState(proveedorInicial ?? null);
  const [buscando, setBuscando] = useState(false);
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [imprimir, setImprimir] = useState(false);
  const terminar = useCallback(() => setImprimir(false), []);
  const q = useCartolaProveedor(empresa!.id, proveedor?.id ?? null, { desde, hasta });
  return (
    <div className="pantalla consulta" data-pantalla="cartola-proveedor">
      <div className="barra-filtros">
        <div className="campo">
          <span className="etiqueta">Proveedor</span>
          <button
            type="button"
            className="selector-cliente"
            aria-label="Elegir proveedor"
            onClick={() => setBuscando(true)}
          >
            {proveedor ? proveedor.razonSocial : 'Buscar proveedor…'}
          </button>
        </div>
        <label className="campo">
          <span className="etiqueta">Desde</span>
          <input
            type="date"
            aria-label="Desde"
            value={desde}
            onChange={(e) => setDesde(e.target.value)}
          />
        </label>
        <label className="campo">
          <span className="etiqueta">Hasta</span>
          <input
            type="date"
            aria-label="Hasta"
            value={hasta}
            onChange={(e) => setHasta(e.target.value)}
          />
        </label>
        <span className="espaciador" />
        {q.data && (
          <span>
            Saldo inicial <b>{pesos(q.data.saldoInicial)}</b> · saldo final{' '}
            <b>{pesos(q.data.saldoFinal)}</b>
          </span>
        )}
        {q.data && (
          <button type="button" onClick={() => setImprimir(true)}>
            Imprimir cartola
          </button>
        )}
      </div>
      {q.isError && <PanelError error={q.error} />}
      {proveedor && (
        <table className="grilla compacta" aria-label="Cartola del proveedor">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Detalle</th>
              <th className="der">Cargo</th>
              <th className="der">Abono</th>
              <th className="der">Saldo</th>
            </tr>
          </thead>
          <tbody>
            {(q.data?.movimientos ?? []).map((m, i) => (
              <tr key={i}>
                <td>{fecha(m.fecha)}</td>
                <td>{m.descripcion}</td>
                <td className="der">{Number(m.cargo) ? pesos(m.cargo) : ''}</td>
                <td className="der">{Number(m.abono) ? pesos(m.abono) : ''}</td>
                <td className="der">
                  <b>{pesos(m.saldo)}</b>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {imprimir && proveedor && (
        <ImprimirCartolaProveedor
          proveedorId={proveedor.id}
          nombre={proveedor.razonSocial}
          desde={desde || undefined}
          hasta={hasta || undefined}
          alTerminar={terminar}
        />
      )}
      {buscando && (
        <BuscadorCliente
          empresaId={empresa!.id}
          rol="proveedor"
          alElegir={(c) => {
            setProveedor({ id: c.id, razonSocial: c.razon_social });
            setBuscando(false);
          }}
          alCerrar={() => setBuscando(false)}
        />
      )}
    </div>
  );
}

// ================================================================ historial

export function HistorialPagos() {
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const [estado, setEstado] = useState<EstadoPago | ''>('');
  const q = usePagos(empresaId, { ...(estado ? { estado } : {}) });
  const proveedor = useNombresProveedores(q.data);
  const accion = useAccionPago(empresaId);
  const [anular, setAnular] = useState<PagoProveedor | null>(null);
  const [motivo, setMotivo] = useState('');
  const [imprimir, setImprimir] = useState<PagoProveedor | null>(null);
  const terminar = useCallback(() => setImprimir(null), []);
  return (
    <div className="pantalla consulta" data-pantalla="historial-pagos">
      <div className="barra-filtros">
        <label className="campo">
          <span className="etiqueta">Estado</span>
          <select
            aria-label="Estado del pago"
            value={estado}
            onChange={(e) => setEstado(e.target.value as EstadoPago | '')}
          >
            <option value="">Todos</option>
            {(Object.keys(NOMBRE_ESTADO_PAGO) as EstadoPago[]).map((k) => (
              <option key={k} value={k}>
                {NOMBRE_ESTADO_PAGO[k]}
              </option>
            ))}
          </select>
        </label>
      </div>
      {q.isError && <PanelError error={q.error} />}
      <Grilla
        etiqueta="Pagos a proveedores"
        columnas={[
          ...columnasPago(proveedor),
          { id: 'comp', titulo: 'Comprobante', celda: (p) => p.comprobante ?? '', ancho: '9rem' },
          {
            id: 'acc',
            titulo: '',
            celda: (p) => (
              <span className="fila-datos">
                <button
                  type="button"
                  className="boton-mini"
                  aria-label={`Imprimir comprobante ${p.numero}`}
                  onClick={() => setImprimir(p)}
                >
                  Imprimir
                </button>
                {(p.estado === 'PAGADO' || p.estado === 'AUTORIZADO') &&
                  puede('tesoreria.pago.anular') && (
                    <button type="button" className="boton-mini" onClick={() => setAnular(p)}>
                      Anular
                    </button>
                  )}
              </span>
            ),
            ancho: '10rem',
          },
        ]}
        filas={q.data ?? []}
        clave={(p) => p.pagoId}
        cargando={q.isLoading}
        vacio="Sin pagos"
      />
      {imprimir && (
        <ImprimirComprobanteEgreso
          pago={imprimir}
          nombre={proveedor(imprimir.proveedorEntidadId)}
          alTerminar={terminar}
        />
      )}
      {anular && (
        <Modal titulo={`Anular pago N° ${anular.numero}`} alCerrar={() => setAnular(null)}>
          <p>
            Las aplicaciones se reversan: los documentos vuelven a deber. Queda registrado quién y
            por qué.
          </p>
          <label className="campo">
            <span className="etiqueta">Motivo</span>
            <input
              aria-label="Motivo de la anulación"
              autoFocus
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
            />
          </label>
          {accion.isError && <PanelError error={accion.error} />}
          <div className="acciones-modal">
            <button type="button" onClick={() => setAnular(null)}>
              Volver
            </button>
            <button
              type="button"
              className="primario"
              disabled={motivo.trim().length < 5 || accion.isPending}
              onClick={() =>
                accion.mutate(
                  {
                    pagoId: anular.pagoId,
                    accion: 'anular',
                    cuerpo: { version: anular.version, motivo: motivo.trim() },
                  },
                  {
                    onSuccess: () => {
                      setAnular(null);
                      setMotivo('');
                    },
                  },
                )
              }
            >
              Anular pago
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
